import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { locationFields } from "../../../../constants/forms/locationFields";
import Form from "../../../../components/forms/Form";
import useBaseService from "../../../../hooks/services/useBaseService";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const CreateCustomerLocationPage = () => {
  const navigate = useNavigate();
  const { id, addressId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  // useBaseService agora expõe setFormErrors para lidar com erros de validação da API
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(locationFields)
  );

  // --- Mutação para criar uma nova Localização ---
  const createLocationMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.customers.customerAddressLocationCreate(id, addressId),
        dataToCreate
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm();
        queryClient.invalidateQueries({
          queryKey: ["customerLocations", id, addressId],
        });
        navigate(`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`, {
          state: { type: "success" },
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        location: {
          ...cleanedData.location,
          spot: String(cleanedData.location.spot),
        },
      };
      await createLocationMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar localização:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createLocationMutation, cleanPayload]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`);
  }, [id, addressId, navigate]);

  // --- Efeito para exibir notificações de estado da rota ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return locationFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Localização"
        showBackButton={true}
        backUrl={`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createLocationMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateCustomerLocationPage;
